<script setup lang="ts">
import ModalComponent from "@/components/ModalComponent.vue";
import ShadowBox from "@/components/ShadowBox.vue";
import StyledButton from "@/components/StyledButton.vue";
import StyledCombobox from "@/components/StyledCombobox.vue";
import StyledInput from "@/components/StyledInput.vue";
import StyledSelect from "@/components/StyledSelect.vue";
import useAuthUser from "@/composables/useAuthUser";
import useInternalRoles from "@/internal/modules/roles/composables/useInternalRoles";
import useCompanies from "@/modules/companies/composables/useCompanies";
import useCompaniesSearch from "@/modules/companies/composables/useCompaniesSearch";
import { Company } from "@/modules/companies/interfaces/company";
import { useQueryClient } from "@tanstack/vue-query";
import useVuelidate from "@vuelidate/core";
import { helpers, required, requiredIf } from "@vuelidate/validators";
import { reactive, watch } from "vue";
import { useToast } from "vue-toastification";
import useRoles from "../composables/useRoles";
import useRoleCompany from "../composables/useRolesCompany";

interface Props {
  visible: boolean;
  companySelected: Company | undefined;
}
interface Emits {
  (e: "actionModal"): void;
}

const { companies, setQuery } = useCompaniesSearch({
  immediate: true,
});

const { user } = useAuthUser();

const props = defineProps<Props>();
const emits = defineEmits<Emits>();
const toast = useToast();

const form = reactive({
  name: "",
  companyId: props.companySelected,
});

const { createRoleCompanyMutationAsync } = useRoleCompany();

const { createRoleAsync } = useInternalRoles({
  immediate: false,
});

const queryClient = useQueryClient();

const rules = {
  name: {
    required: helpers.withMessage("El nombre del rol es requerido", required),
  },
  companyId: {
    required: helpers.withMessage(
      "Seleccione una empresa",
      requiredIf(() => !user?.value?.internal),
    ),
  },
};

const v$ = useVuelidate(rules, form);

const handleSubmit = async () => {
  for (const field of v$.value.$silentErrors) {
    if (!Array.isArray(field.$message)) {
      console.log(field.$message);

      toast.error(field.$message);
      return;
    }
  }

  if (form.companyId) {
    await createRoleCompanyMutationAsync({
      name: form.name,
      companyId: Number(form.companyId?.id),
    });

    await queryClient.invalidateQueries(["rolesCompany"]);
  } else {
    await createRoleAsync({
      name: form.name,
    });

    toast.success("Rol creado correctamente");

    await queryClient.invalidateQueries(["internalRoles"]);
  }

  emits("actionModal");

  form.name = "";
  form.companyId = undefined;
};
</script>

<template>
  <ModalComponent
    :visible="props.visible"
    :modelValue="props.visible"
    @update:model-value="emits('actionModal')"
  >
    <template #modalBody>
      <ShadowBox class="bg-white">
        <h3 class="text-lg font-semibold pb-1 border-b">Crear Rol</h3>

        <form @submit.prevent="handleSubmit" class="flex flex-col gap-2">
          <StyledInput label="Nombre del rol" v-model="form.name" />

          <div v-if="!user?.internal">
            <StyledCombobox
              v-if="!props.companySelected"
              v-model="form.companyId"
              :options="
                companies.map((company) => ({
                  id: company.id,
                  name: company.name,
                }))
              "
              label="Empresa"
              @input-change="setQuery"
              :show-input="!form.companyId"
              :placeholder="`${!form.companyId ? (user?.internal ? 'Rol interno' : 'Seleccione una empresa') : 'Seleccione una empresa'}`"
            >
              <template #default="{ option }">
                <p>
                  <span class="font-semibold">{{ option.name }}</span>
                </p>
              </template>

              <template #input-overlap>
                <p>
                  <span class="font-semibold">{{ form.companyId?.name }}</span>
                </p>

                <button
                  class="material-symbols-outlined text-[22px]"
                  @click="form.companyId = undefined"
                >
                  close
                </button>
              </template>
            </StyledCombobox>
          </div>

          <div class="flex justify-end mt-4 gap-3">
            <StyledButton
              class="bg-red-600 hover:bg-red-800 text-white"
              label="Volver"
              type="button"
              @click="emits('actionModal')"
            />
            <StyledButton class="bg-blue-600 text-white" label="Crear" />
          </div>
        </form>
      </ShadowBox>
    </template>
  </ModalComponent>
</template>
